import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import React, { useState } from 'react'
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom'
import { useMount } from 'react-use'

import { Background, Flex, Spinner } from './Components'
import { ErrorHandler, Login, ProtectedRoute } from './Pages'
import { PanelOperationsAdmin } from './Pages/Admin/PanelOperations'
import { AdminSettings } from './Pages/Admin/Settings'
import { SocietyEditor } from './Pages/Admin/Society/Editor'
import { SocietyList } from './Pages/Admin/Society/List'
import { TravellerEditor } from './Pages/Admin/Travellers/Editor'
import { TravellersImport } from './Pages/Admin/Travellers/Import'
import { TravellersList } from './Pages/Admin/Travellers/List'
import { UserEditor } from './Pages/Admin/Users/Editor'
import { UsersList } from './Pages/Admin/Users/List'
import { AdminCoefficient } from './Pages/AdminCoefficient'
import { Brochure } from './Pages/Brochure'
import { NewChat } from './Pages/Chat/NewChat'
import { TestChat } from './Pages/Chat/TestChat'
import { CommunityPanel } from './Pages/CommunityPanel'
import { CommunityPresetsSearch } from './Pages/CommunityPanel/Search'
import { ChatFiera } from './Pages/Fiera/ChatFiera'
import { GigiForm } from './Pages/Gigi/GigiForm'
import { GigiList } from './Pages/Gigi/GigiList'
import { ProcessCompare } from './Pages/Process/Compare'
import { ConversationsList } from './Pages/Process/Conversations'
import { ProcessStepsVisualizer } from './Pages/Process/Editor'
import { ProcessesErrorStats } from './Pages/Process/ErrorStats'
import { ProcessList } from './Pages/Process/List'
import { PerformanceDashboard } from './Pages/Process/Performance'
import { ConversationPricing } from './Pages/Process/Pricing'
import { ProcessStepsStats } from './Pages/Process/Stats'
import TabPrompts from './Pages/Prompts'
import NewPrompt from './Pages/Prompts/NewPrompt'
import { Statistics } from './Pages/Statistics'
import { DashboardChartInfoPricing } from './Pages/Statistics/ChartInfoPricing'
import { AutoTest } from './Pages/Test/AutoTest'
import { AutoTestsList } from './Pages/Test/AutoTestList'
import { TestDyn } from './Pages/TestDyn'
import AuthProvider, { useAuth } from './Provider/AuthProvider'
import { FC } from './Services'
import { cap } from './State'

export const queryClient = new QueryClient()

const LightTheme = React.lazy(() => import('./Themes/Light/Theme'))
const DarkTheme = React.lazy(() => import('./Themes/Dark/Theme'))
const ReactQueryDevtoolsProduction = React.lazy(() =>
  import('@tanstack/react-query-devtools/build/modern/production.js').then((d) => ({
    default: d.ReactQueryDevtools
  }))
)

const ThemeSelector = ({ children }) => {
  const [darkMode] = cap.darkMode.useState()
  React.useEffect(() => {
    // Propaga il tema su <html> per far ereditare i token CSS anche ai portali
    // PrimeReact (dropdown, dialog, tooltip) che vengono montati su document.body
    // fuori dall'albero React e altrimenti vedrebbero solo i token light.
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light')
  }, [darkMode])
  return (
    <>
      <React.Suspense fallback={<></>}>{darkMode ? <DarkTheme /> : <LightTheme />}</React.Suspense>
      {children}
    </>
  )
}

function AppSetup() {
  const { user: storedUser, onLoginJWT } = useAuth()
  const [loading, setLoading] = useState(true)
  const [showDevtools, setShowDevtools] = useState(false)

  useMount(async () => {
    if (FC.authenticated && !storedUser?.accessToken) {
      setLoading(false)
      return true
    }
    storedUser?.accessToken && (await onLoginJWT(storedUser?.accessToken))
    setLoading(false)
  })

  React.useEffect(() => {
    const sequence = ['s', 'i', 'a', 'p', 'ArrowLeft', 'ArrowLeft', 'ArrowRight', 'ArrowRight']
    let index = 0
    const onKeyDown = (e) => {
      const key = e.key?.length === 1 ? e.key.toLowerCase() : e.key
      if (key === sequence[index]) {
        index += 1
        if (index === sequence.length) {
          setShowDevtools((old) => !old)
          index = 0
        }
        return
      }
      index = key === sequence[0] ? 1 : 0
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <ThemeSelector>
      <Background whiteBackground allowScroll>
        <ReactQueryDevtools initialIsOpen={false} />
        {showDevtools && (
          <React.Suspense fallback={null}>
            <ReactQueryDevtoolsProduction />
          </React.Suspense>
        )}
        {loading ? (
          <Flex fw fh>
            <Spinner />
          </Flex>
        ) : (
          <Outlet />
        )}
      </Background>
    </ThemeSelector>
  )
}

const CheckRoute = ({ children, role = [] }) => {
  const { user: userObj = {} } = useAuth()
  const { user = {} } = userObj || {}
  if (role?.length && !role.includes(user?.role)) {
    if (user.role === 'dynamicTester') return <Navigate to="/testDyn" />
    return <Navigate to="/operatore/list/" />
  }
  return children
}

const router = createBrowserRouter([
  {
    element: <AuthProvider />,
    errorElement: <ErrorHandler />,
    children: [
      {
        element: <AppSetup />,
        children: [
          {
            element: <ProtectedRoute />,
            children: [
              {
                path: '*',
                element: (
                  <CheckRoute>
                    {' '}
                    <Navigate to="/operatore/list/" />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/chat/*',
                element: (
                  <CheckRoute role={['admin', 'tester']}>
                    {' '}
                    <Navigate to="/chat/new" />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/chat/new',
                element: (
                  <CheckRoute role={['admin', 'tester']}>
                    {' '}
                    <NewChat />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/chat/fiera',
                element: (
                  <CheckRoute role={['admin']}>
                    <ChatFiera />
                  </CheckRoute>
                )
              },
              {
                path: '/conversations/*',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <Navigate to="/conversations/list" />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/conversations/list',
                element: (
                  <CheckRoute role={['admin']}>
                    <ConversationsList />
                  </CheckRoute>
                )
              },
              {
                path: '/conversations/:conversationId',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <ProcessList />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/pricing/:conversationId',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <ConversationPricing />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/community/search',
                element: (
                  <CheckRoute role={['admin', 'dynamicTester', 'tester', 'operatorCommunity']}>
                    {' '}
                    <CommunityPresetsSearch />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/community/panel/:presetId',
                element: (
                  <CheckRoute role={['admin', 'dynamicTester', 'tester', 'operatorCommunity']}>
                    {' '}
                    <CommunityPanel />
                  </CheckRoute>
                )
              },
              {
                path: '/community/panel/:operation/:presetId',
                element: (
                  <CheckRoute role={['admin', 'dynamicTester', 'tester', 'operatorCommunity']}>
                    {' '}
                    <CommunityPanel />
                  </CheckRoute>
                )
              },
              {
                path: '/operatore/*',
                element: (
                  <CheckRoute role={['admin', 'operator', 'tester', 'operatorCommunity']}>
                    {' '}
                    <Navigate to="/operatore/list" />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/operatore/list/',
                element: (
                  <CheckRoute role={['admin', 'operator', 'tester', 'operatorCommunity']}>
                    <GigiList />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/operatore/:conversationId',
                element: (
                  <CheckRoute role={['admin', 'operator', 'tester', 'operatorCommunity']}>
                    <GigiForm />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/testChat/*',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <Navigate to="/testChat/new" />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/testChat/new',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <TestChat />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/admin/*',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <Navigate to="/admin/society/list" />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/admin/society/list',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <SocietyList />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/admin/society/:societyId',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <SocietyEditor />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/admin/users/list',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <UsersList />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/admin/users/:userId',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <UserEditor />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/admin/travellers/list',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <TravellersList />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/admin/travellers/import',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <TravellersImport />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/admin/travellers/:travellerId',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <TravellerEditor />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/admin/coefficient',
                element: (
                  <CheckRoute role={['admin', 'dynamicTester']}>
                    {' '}
                    <AdminCoefficient />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/admin/panelOperations',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <PanelOperationsAdmin />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/admin/settings',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <AdminSettings />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/testDyn',
                element: (
                  <CheckRoute role={['admin', 'dynamicTester', 'tester']}>
                    {' '}
                    <TestDyn />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/process/*',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <Navigate to="/process/performance" />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/prompts/list',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <TabPrompts />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/prompts/:promptId',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <NewPrompt />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/process/performance',
                element: (
                  <CheckRoute role={['admin']}>
                    <PerformanceDashboard />
                  </CheckRoute>
                )
              },
              {
                path: '/process/errorStats',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <ProcessesErrorStats />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/process/:processId',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <ProcessStepsVisualizer />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/process/stats/:processId',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <ProcessStepsStats />
                  </CheckRoute>
                )
              },
              {
                path: '/process/compare/:processId',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <ProcessCompare />
                  </CheckRoute>
                )
              },
              {
                path: '/autotest',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <Navigate to="/autotest/list" />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/autotest/list',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <AutoTestsList />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/autotest/:autoTestId',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <AutoTest />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/statistics/',
                element: (
                  <CheckRoute role={['admin', 'operatorCommunity', 'fatturazione']}>
                    {' '}
                    <Statistics />{' '}
                  </CheckRoute>
                )
              },
              {
                path: '/dashboardPricing/',
                element: (
                  <CheckRoute role={['admin']}>
                    {' '}
                    <DashboardChartInfoPricing />{' '}
                  </CheckRoute>
                )
              }
            ]
          },
          (window?.location?.origin === 'https://nora.siap.cloud' ||
            window?.location?.origin === 'http://localhost:4000') && {
            path: '/',
            element: <Brochure />
          },
          { path: '/login', element: <Login /> }
        ]
      }
    ]
  }
])

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  )
}
export default App
